import { createGlobalStyle, css } from 'styled-components';
import { type themeProps } from '../../utils/theme';

const GlobalStyle = createGlobalStyle<{ theme: themeProps }>`
	:root {
    // Real Colors
    --color-regal-blue: #003C71;
    --color-regal-blue-50: rgba(0, 60, 113, .5);
    --color-congress-blue: #00418A;
    --color-catalina-blue: #083C71;
    --color-denim: #1A70B8;
    --color-curious-blue: #289DDF;
    --color-cerulean: #009FE3;
    --color-cerulean-light: #00A3E0;
    --color-regent-st-blue: #A3D8E8;
    --color-white: #FFFFFF;
    --color-red: #FF0000;
    --color-turbo: #FDE600;
    --color-school-bus-yellow: #FEDD00;
    --color-kournikova: #FFED73;
    --color-buddha-gold: #D39003;
		
    // Abstract Colors
		${(props) =>
      props.theme.color === 'yellow'
        ? css`
            --color-brand-primary: var(--color-cerulean-light);
            --color-brand-secondary: var(--color-kournikova);
            --color-brand-tertiary: var(--color-regal-blue);
            --color-accent-primary: var(--color-cerulean);
            --color-accent-secondary: var(--color-turbo);
            --color-accent-tertiary: var(--color-buddha-gold);
            --color-accent-text: var(--color-white);
            --color-accent-background: var(--color-kournikova);
          `
        : css`
            --color-brand-primary: var(--color-school-bus-yellow);
            --color-brand-secondary: var(--color-kournikova);
            --color-brand-tertiary: var(--color-regal-blue);
            --color-accent-primary: var(--color-turbo);
            --color-accent-secondary: var(--color-cerulean);
            --color-accent-tertiary: var(--color-congress-blue);
            --color-accent-text: var(--color-cerulean-light);
            --color-accent-background: var(--color-regent-st-blue);
          `};
    --color-error: var(--color-red);

    // Specific Colors

    // Background
    --color-background: var(--color-accent-background);
    --color-background-accent: var(--color-white);

    // Text
    --color-text-primary: var(--color-regal-blue);

    //Headline
    --color-headline-primary: var(--color-text-primary);
    --color-headline-error: var(--color-error);

    //Copy
    --color-copy-text: var(--color-text-primary);
    
    // Input
    --color-input-text: var(--color-regal-blue);
    --color-input-placeholder: var(--color-regal-blue-50);
    --color-input-background: var(--color-school-bus-yellow);
    --color-input-background-hover: var(--color-turbo);
    --color-input-background-focus: var(--color-turbo);
    --color-input-border: var(--color-school-bus-yellow);
    --color-input-border-hover: var(--color-turbo);
    --color-input-border-focus: var(--color-turbo);
    --color-input-outline: var(--color-regal-blue);

    // Button
    --color-button-text-primary: var(--color-regal-blue);
    --color-button-background-primary: var(--color-school-bus-yellow);
    --color-button-background-hover-primary: var(--color-turbo);
    --color-button-background-focus-primary: var(--color-turbo);
    --color-button-border-primary: var(--color-school-bus-yellow);
    --color-button-border-hover-primary: var(--color-turbo);
    --color-button-border-focus-primary: var(--color-turbo);
    --color-button-outline-primary: var(--color-regal-blue);

    --color-button-text-secondary: var(--color-accent-text);
    --color-button-background-secondary: var(--color-brand-primary);
    --color-button-background-hover-secondary: var(--color-accent-primary);
    --color-button-background-focus-secondary: var(--color-accent-primary);
    --color-button-border-secondary: var(--color-brand-primary);
    --color-button-border-hover-secondary: var(--color-accent-primary);
    --color-button-border-focus-secondary: var(--color-accent-primary);
    --color-button-outline-secondary: var(--color-regal-blue);

    // Font Family
    --font-family-graphik: Graphik, sans-serif;
    --font-family-camel: Camel;

    // Font Weight
    --font-weight-regular: 400;
    --font-weight-bold: 700;

    // Font Size
    --font-size-xxs: 12px;
    --font-size-xs: 14px;
    --font-size-s: 16px;
    --font-size-m: 20px;
    --font-size-l: 24px;
    --font-size-xl: 33px;
    --font-size-xxl: 70px;

    // Line Height
    --line-height-4xs: 16px;
    --line-height-3xs: 18px;
    --line-height-xxs: 21px;
    --line-height-xs: 24px;
    --line-height-s: 28px;
    --line-height-m: 32px;
    --line-height-l: 33px;
    --line-height-xl: 38px;
    --line-height-xxl: 40px;
    --line-height-3xl: 42px;
    --line-height-4xl: 70px;

    // Distances
    --distance-5xs: 6px;
    --distance-4xs: 10px;
    --distance-3xs: 12px;
    --distance-xxs: 16px;
    --distance-xs: 18px;
    --distance-s: 20px;
    --distance-m: 30px;
    --distance-l: 36px;
    --distance-xl: 50px;
    --distance-xxl: 60px;
    --distance-3xl: 80px;
    --distance-4xl: 120px;

    // Sizes
    --logo-width: 130px;
    --scrollto-width: 86px;
    --cookie-width: 64px;

    // Max Widths
    --max-width-s: 660px;
    --max-width-m: 732px;

    // Border Radius
    --border-radius-s: 10px;
    --border-radius-m: 30px;
  }

  body {
    margin: 0;
    font-family: var(--font-family-graphik);
    color: var(--color-text-primary);
  }

  html {
    scroll-behavior: smooth;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  @font-face {
      font-family: 'Graphik';
      src: url('fonts/graphikregular.woff2') format('woff2'),
        url('fonts/graphikregular.woff') format('woff');
      font-weight: 400;
      font-display: swap;
    }

    @font-face {
      font-family: 'Graphik';
      src: url('fonts/graphikbold.woff2') format('woff2'),
        url('fonts/graphikbold.woff') format('woff');
      font-weight: 700;
      font-display: swap;
    }

    @font-face {
      font-family: 'Camel';
      src: url('fonts/camel-light.woff2') format('woff2'),
        url('fonts/camel-light.woff') format('woff');
      font-weight: 300;
      font-display: swap;
    }

    @font-face {
      font-family: 'Camel';
      src: url('fonts/camel-regular.woff2') format('woff2'),
        url('fonts/camel-regular.woff') format('woff');
      font-weight: 400;
      font-display: swap;
    }

    @font-face {
      font-family: 'Camel';
      src: url('fonts/camel-bold.woff2') format('woff2'),
        url('fonts/camel-bold.woff') format('woff');
      font-weight: 700;
      font-display: swap;
    }
`;

export default GlobalStyle;
